import { useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import analytics from "../helper/analytics";

const OFFER_ACCENT = "#B45309"; // warm amber for the "savings" theme

// Fallback badge when the backend omits `badge` — first letter of the title on amber.
const fallbackBadge = (offer) => ({
  label: (offer?.title?.trim()?.[0] || "%").toUpperCase(),
  bg: "#B45309",
  fg: "#fff",
});

const Badge = ({ badge, size = 34 }) => (
  <div
    className="flex-shrink-0 rounded-lg flex items-center justify-center font-extrabold"
    style={{ width: size, height: size, background: badge.bg, color: badge.fg, fontSize: size * 0.42 }}
  >
    {badge.label}
  </div>
);

// `offers` comes from the payment data response (`data.offers`). Each offer:
// { id, title, tnc, badge{label,bg,fg}, logo, code, method }. The section
// auto-hides when the list is empty or absent.
const Offers = ({ offers }) => {
  const [open, setOpen] = useState(false);

  const list = Array.isArray(offers) ? offers : [];
  if (!list.length) return null;

  const first = list[0];
  const rest = list.length - 1;

  const openSheet = () => {
    analytics.track("offers_viewed", { count: list.length });
    setOpen(true);
  };

  return (
    <>
      {/* ── "Available Offers" strip ── */}
      <div className="px-2 pt-2 pb-1">
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Available Offers</p>
        <div className="flex items-center gap-2">
          {/* Lead offer chip */}
          <button
            type="button"
            onClick={openSheet}
            className="flex-1 min-w-0 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full transition-all active:scale-[0.98]"
            style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.28)" }}
          >
            <Badge badge={first.badge || fallbackBadge(first)} size={26} />
            <span className="truncate text-[12.5px] font-medium text-gray-700 text-left">{first.title}</span>
          </button>

          {/* View all */}
          {rest > 0 && (
            <button
              type="button"
              onClick={openSheet}
              className="flex-shrink-0 flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full transition-all active:scale-[0.98]"
              style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.28)" }}
            >
              <span
                className="flex items-center justify-center rounded-full text-[10px] font-bold"
                style={{ width: 22, height: 22, background: "#fff", color: OFFER_ACCENT, border: "1px solid rgba(245,158,11,0.4)" }}
              >
                +{rest}
              </span>
              <span className="text-[12.5px] font-bold" style={{ color: OFFER_ACCENT }}>View all</span>
            </button>
          )}
        </div>
      </div>

      {/* ── "Save more" bottom sheet ── */}
      {open && (
        <div className="fixed inset-0 z-[9998] flex items-end justify-center" onClick={() => setOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0" style={{ background: "rgba(10,25,60,0.45)", backdropFilter: "blur(2px)" }} />

          {/* Sheet */}
          <div
            className="relative w-full max-w-[480px] bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[82vh]"
            style={{ animation: "swink-sheet-up 0.32s cubic-bezier(0.22,1,0.36,1) both" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grabber */}
            <div className="flex justify-center pt-2.5 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Header */}
            <div className="flex items-start justify-between px-5 pt-2 pb-3">
              <div>
                <h3 className="text-xl font-extrabold text-gray-900 leading-tight">Save more</h3>
                <p className="text-sm text-gray-400">with payment offers</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 active:scale-90 transition-all"
              >
                <AiOutlineClose className="text-gray-500 text-sm" />
              </button>
            </div>

            {/* Offer list */}
            <div className="flex-1 overflow-y-auto px-4 pb-6 pt-1 hide-scrollbar flex flex-col gap-3">
              {list.map((o) => (
                <div key={o.id} className="rounded-2xl border border-gray-200 overflow-hidden">
                  {/* Body */}
                  <div className="flex items-start gap-3 px-3.5 pt-3.5 pb-3">
                    <Badge badge={o.badge || fallbackBadge(o)} size={34} />
                    <p className="flex-1 text-[14px] font-semibold text-gray-800 leading-snug">{o.title}</p>
                  </div>
                  {/* T&Cs footer */}
                  <div className="px-3.5 py-2.5" style={{ background: "rgba(245,158,11,0.06)" }}>
                    <p className="text-[12px] text-gray-500 leading-snug">
                      {o.tnc} • <span className="font-bold text-gray-600">T&amp;Cs</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Offers;
